<template>
  <article class="panel panel-section request-assigned-panel p-bottom-4">
    <p
      class="panel-heading request-assigned-panel-header has-background-light has-text-weight-semibold is-size-4"
    >
      Staff assigned
    </p>
    <div class="request-assigned-panel-content" v-if="!filteredRequestLoading">
      <RequestDepartmentAssigned />
      <RequestPoc :poc="poc" />
      <div v-if="canSeeStaffOnlyItems" data-cy="request-staff">
        <div
          class="panel-item is-flex is-align-items-center is-justify-content-space-between has-text-weight-semibold panel-item--editable"
        >
          Support staff
          <NrTooltip v-if="canAddAndRemoveStaff" label="Add support staff">
            <NrIconButton
              class="edit-request-assigned-btn"
              variant="minimal-neutral"
              :icon="{ name: 'plus', type: 'regular' }"
              @click="handleAddStaffClick()"
              aria-label="add support staff"
            />
          </NrTooltip>
        </div>
        <div
          class="is-flex is-align-items-center is-justify-content-space-between panel-item"
          :class="canAddAndRemoveStaff ? 'panel-item--editable' : 'p-bottom-8'"
          v-for="(staff, index) in requestStaff"
          :key="index"
        >
          <NrTooltip
            v-if="staff.undeliverable_email"
            label="Mail to this address has been returned as undeliverable"
          >
            <p class="m-left-8 has-text-red t--strikethrough">
              {{ staffNameOrEmail(staff) }}
            </p>
          </NrTooltip>

          <div v-else-if="staff && staff.departments.length">
            <NrTooltip
              v-if="staffDepartmentsWithPocLabel(staff) != ''"
              :label="staffDepartmentsWithPocLabel(staff)"
            >
              <p class="m-left-8">
                {{ staffNameOrEmail(staff) }}
              </p>
            </NrTooltip>
            <p v-else class="m-left-8">
              {{ staffNameOrEmail(staff) }}
            </p>
          </div>

          <p v-else-if="!staff.undeliverable_email" class="m-left-8">
            {{ staffNameOrEmail(staff) }}
          </p>

          <NrTooltip v-if="canAddAndRemoveStaff" label="Remove Staff">
            <NrIconButton
              class="edit-request-assigned-btn"
              variant="minimal-neutral"
              :icon="{ name: 'trash-alt', type: 'regular' }"
              @click="handleRemoveStaffClick(staff)"
              aria-label="Remove staff"
            />
          </NrTooltip>
        </div>
        <NrModal
          class="remove-staff-modal"
          v-model="selectedStaff"
          title="Remove Staff"
          :show="showRemoveStaffModal"
          :hasSecondaryButton="true"
          secondaryButtonText="Cancel"
          primaryButtonText="Remove"
          :disableButtons="disableRemoveStaffButton"
          @on-primary-click="submitRemoveStaff"
          @on-secondary-click="hideRemoveStaffModal"
          @on-close-click="hideRemoveStaffModal"
        >
          {{ removeStaffDisplayText }}
        </NrModal>
        <div>
          <message-box
            v-if="showAddStaffModal"
            title="Add Staff"
            type="default"
            submit-text="Add Staff"
            :disable-submit="false"
            @on-close="hideAddStaffModal"
            @on-cancel="hideAddStaffModal"
            @on-submit="submitAddStaff"
          >
            <div class="message-contents-field">
              <NrSelect
                class="m-top-12"
                v-model="addStaffToRequest"
                placeholder="Search names or emails"
                :options="filteredStaff"
                @load-more="handleStaffScroll"
                @search="filterStaff"
                @open="filterStaff"
                multiple
                :loading="dataFetchState.staff.fetching"
                :hasMore="!dataFetchState.staff.end"
              >
                <template v-slot:label>Search staff</template>
              </NrSelect>
              <label>
                <span class="has-text-weight-bold"
                  >Send Message (optional)</span
                >
                <nr-editor
                  v-model="message"
                  :resizable="false"
                  :bold="false"
                  :underline="false"
                  :italic="false"
                  class="add-staff-modal"
                  :placeholder="'Send an optional message to inform staff why they are being assigned to this request'"
                  :rows="10"
                />
              </label>
              <div class="m-top-12" v-if="message">
                <NrFormInput
                  type="checkbox"
                  label="Send to all assigned staff"
                  name="send_all_assigned_staff"
                  v-model="sendAllAssignedStaff"
                />
              </div>
            </div>
          </message-box>
        </div>
      </div>
    </div>
    <NrLoadingAnimation v-else :count="3" />
  </article>
</template>

<script lang="ts">
import { throttle } from 'lodash'
import { subject } from '@casl/ability'
import API from '../../../vue_shared/api'
import { mapActions, mapState } from 'vuex'
import MessageBox from '@nextrequestco/components/src/components/MessageBox'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import RequestDepartmentAssigned from '../components/RequestDepartmentAssigned.vue'
import RequestPoc from '../components/RequestPoc.vue'

interface Staff {
  id: number
  name_with_email: string // eslint-disable-line camelcase
  name_or_email: string // eslint-disable-line camelcase
  departments: string[]
}

export default {
  name: 'RequestAssigned',
  components: {
    MessageBox,
    NrIconButton,
    NrFormInput,
    'nr-editor': NrEditor,
    NrModal,
    NrTooltip,
    RequestPoc,
    NrSelect,
    NrLoadingAnimation,
    RequestDepartmentAssigned
  },
  props: {
    account: {
      type: Object
    },
    requestId: {
      type: String
    },
    departments: {
      type: Array
    },
    visibility: {
      type: String
    },
    prettyId: {
      type: String
    },
    poc: {
      type: Object
    },
    requestStaff: {
      type: Array
    }
  },
  data() {
    return {
      addStaffToRequest: [],
      message: '',
      name: '',
      page: 1,
      selectedStaff: [],
      showAddStaffModal: false,
      showRemoveStaffModal: false,
      staffNames: [{ id: '', name: '' }],
      endFetching: false,
      staffHasIncompleteTasks: false,
      sendAllAssignedStaff: false
    }
  },
  computed: {
    ...mapState('request_show', [
      'request',
      'dataFetchState',
      'dataOptions',
      'filteredRequestLoading'
    ]),

    canSeeStaffOnlyItems() {
      return this.$can('read', 'staff_only_items')
    },

    canAddAndRemoveStaff() {
      return (
        this.$ability.can('manage', 'Request') ||
        this.$ability.can('read_helpers', subject('Request', this.request))
      )
    },

    disableRemoveStaffButton() {
      return this.staffHasIncompleteTasks
    },

    removeStaffDisplayText() {
      if (this.staffHasIncompleteTasks) {
        return `${this.staffNameOrEmail(
          this.selectedStaff[0]
        )} has incomplete tasks.
         Please reassign their tasks or mark as complete before attempting to remove
         this staff member from the request.`
      } else {
        return `You are about to remove ${this.staffNameOrEmail(
          this.selectedStaff[0]
        )} from support staff for this request. Do you want to continue?`
      }
    },

    filteredStaff() {
      const filteredWithoutAssignedStaff = this.dataOptions.staff.filter(
        (staff: Staff) =>
          !this.requestStaff.some((assigned: Staff) => assigned.id === staff.id)
      )

      return filteredWithoutAssignedStaff.map((staff: Staff) => ({
        label: staff.name_with_email,
        value: staff.id
      }))
    }
  },
  methods: {
    ...mapActions('request_show', ['submitStaffChange', 'fetchDataOptions']),

    staffNameOrEmail(staff: Staff): string {
      if (!staff) return ''
      return staff.name_or_email
    },

    staffDepartmentsWithPocLabel(staff: Staff): string {
      const departmentsList = []
      this.departments.forEach((department) => {
        if (staff.departments.includes(department.name)) {
          if (department.poc_id === staff.id) {
            departmentsList.unshift(department.name + ' (Point of Contact)')
          } else {
            departmentsList.push(department.name)
          }
        }
      })
      return departmentsList.join(', ')
    },

    filterStaff: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'staff' })
    }, 400),

    handleStaffScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.staff.search_text,
        dataType: 'staff'
      })
    }, 400),

    handleAddStaffClick() {
      this.showAddStaffModal = true
    },

    async handleRemoveStaffClick(staff: Staff) {
      this.showRemoveStaffModal = true
      this.selectedStaff = [staff]
      try {
        const response = await API.fetchRequestTasks({
          pretty_id: this.request.pretty_id,
          params: {
            user_id: staff.id,
            check_incomplete_tasks: true
          }
        })
        this.staffHasIncompleteTasks = response.data.incomplete_tasks
      } catch (_error) {
        this.$toasted.error('An error has occured. Please try again.')
      }
    },

    hideRemoveStaffModal() {
      this.showRemoveStaffModal = false
      this.selectedStaff = []
    },

    hideAddStaffModal() {
      this.addStaffToRequest = []
      this.message = ''
      this.name = ''
      this.page = 1
      this.selectedStaff = []
      this.showAddStaffModal = false
      this.staffNames = [{ id: '', name: '' }]
      this.endFetching = false
      this.sendAllAssignedStaff = false
    },

    submitAddStaff() {
      this.submitStaffChange({
        method: 'post',
        action: 'update',
        request_id: this.prettyId,
        staff_ids: this.addStaffToRequest.map(
          (staff: { value: number }) => staff.value
        ),
        message: this.message,
        send_all_assigned_staff: this.sendAllAssignedStaff
      }).then(() => {
        this.hideAddStaffModal()
      })
    },

    submitRemoveStaff() {
      this.submitStaffChange({
        method: 'delete',
        action: 'remove',
        request_id: this.prettyId,
        id: this.selectedStaff[0].id
      }).then(() => {
        this.hideRemoveStaffModal()
      })
    }
  }
}
</script>

<style lang="scss">
.request-assigned-panel {
  .panel-section {
    margin-left: -0.75em;
    margin-right: -0.75em;
    padding: 0 0.75em;
  }
  .is-align-items-center {
    align-items: center;
  }
  .is-justify-content-space-between {
    justify-content: space-between;
  }
  .remove-staff-modal {
    .nr-modal__header {
      background-color: #e02b00;
      color: white;
    }
    .nr-modal__title {
      font-weight: bold;
      color: #fff;
    }
    .nr-modal__footer button:last-child {
      background-color: red;
      color: white;
    }
  }
  .add-staff-modal {
    border: 0.5px solid #d1d1d1;
  }

  .message-contents-field .vs__dropdown-menu {
    max-height: 300px;
  }
}
</style>
